import { useCallback, useState } from "react";

const VENDOR_ID_RASPBERRY_PI = 0x2e8a;
const PRODUCT_ID_RP2040_USBBOOT = 0x0003;
const PRODUCT_ID_RP2040_STDIO_USB = 0x000a;

// From https://github.com/raspberrypi/pico-sdk/blob/master/src/common/pico_usb_reset_interface_headers/include/pico/usb_reset_interface.h
// VENDOR sub-class for the reset interface
const RESET_INTERFACE_SUBCLASS = 0x00;
// VENDOR protocol for the reset interface
const RESET_INTERFACE_PROTOCOL = 0x01;

// CONTROL requests:

// reset to BOOTSEL
const RESET_REQUEST_BOOTSEL = 0x01;
// regular flash boot
const RESET_REQUEST_FLASH = 0x02;

const Firmware = () => {
  const [message, setMessage] = useState("");
  
  const onClickFlash = useCallback(async () => {
    // Load the firmware uf2 file
    const firmwareUf2Buf = await (await fetch("/percy.uf2")).arrayBuffer();
    const firmwareUf2 = new DataView(firmwareUf2Buf);

    // Parse the firmware uf2 file
    const FLASH_SECTOR_SIZE = 4096;
    const PAGE_SIZE = 256;
    
    const numBlocks = firmwareUf2.getUint32(24, true);
    const firmwareWriteBuf = new Uint8Array(numBlocks * 256);
    let minSector = Infinity;
    let maxSector = -Infinity;
    console.log(`${numBlocks} blocks`);
    for (let i = 0; i < numBlocks; i++) {
      const blockStart = i*512;
      const flags = firmwareUf2.getUint32(blockStart + 8, true);
      const targetAddr = firmwareUf2.getUint32(blockStart + 12, true);
      // const payloadSize = firmwareUf2.getUint32(blockStart + 16, true);
      const payload = new Uint8Array(firmwareUf2.buffer, blockStart + 32, 256);

      const targetSector = Math.floor(targetAddr / FLASH_SECTOR_SIZE);
      // const targetOffset = targetAddr % FLASH_SECTOR_SIZE;

      minSector = Math.min(minSector, targetSector);
      maxSector = Math.max(maxSector, targetSector);

      firmwareWriteBuf.subarray(256*i).set(payload);

      // console.log(`Block ${i}`);
      // console.log(`  flags: ${flags}`);
      // console.log(`  targetAddr: ${targetAddr} (sector ${targetSector}, offset ${targetOffset})`);
      // console.log(`  payloadSize: ${payloadSize}`);
    }

    // Request the device
    let device;
    try {
      device = await navigator.usb.requestDevice({
        filters: [{
          vendorId: VENDOR_ID_RASPBERRY_PI,
        }],
      });
    } catch {
      return;
    }

    // Automatically reset the device if it is in the application code
    let wasReset = false;
    if (device.productId === PRODUCT_ID_RP2040_STDIO_USB) {
      console.log("Device appears to be a RP2040 device with a USB serial connection, not in BOOTSEL mode. Rebooting into BOOTSEL mode...");

      const picoUsbResetInterface = device.configuration.interfaces
        .find(iface => (
          (iface.alternate.interfaceClass === 0xFF) &&
          (iface.alternate.interfaceSubclass === RESET_INTERFACE_SUBCLASS) &&
          (iface.alternate.interfaceProtocol === RESET_INTERFACE_PROTOCOL)
        ));

      if (picoUsbResetInterface === undefined) {
        console.log("Unable to find the pico usb reset interface");
        setMessage("Unable to find the pico usb reset interface");
        return;
      }

      await device.open();
      await device.claimInterface(picoUsbResetInterface.interfaceNumber);

      // NOTE(Jon @ Fyelabs): This fails since the device reboots
      try {
        await device.controlTransferOut({
          requestType: 'class',
          recipient: 'interface',
          request: RESET_REQUEST_BOOTSEL,
          value: 0,
          index: picoUsbResetInterface.interfaceNumber,
        });
      } catch {}

      // Reconnect now that the device has reset
      try {
        device = await navigator.usb.requestDevice({
          filters: [{
            vendorId: VENDOR_ID_RASPBERRY_PI,
          }],
        });
      } catch {
        setMessage("Failed to reconnect");
        return;
      }
      wasReset = true;
    }
    
    // Handle picoboot mode
    if (device.productId === PRODUCT_ID_RP2040_USBBOOT) {
      console.log("Device appears to be a RP2040 device in BOOTSEL mode.");

      const picobootInterface = device.configuration.interfaces
        .find(iface => (
          (iface.alternate.interfaceClass === 0xFF) &&
          (iface.alternate.interfaceSubclass === 0) &&
          (iface.alternate.interfaceProtocol === 0)
        ));
      
      const picobootBulkOut = picobootInterface.alternate.endpoints
        .find(ep => ep.direction === 'out');

      const picobootBulkIn = picobootInterface.alternate.endpoints
        .find(ep => ep.direction === 'in');

      if (picobootInterface === undefined || picobootBulkOut === undefined || picobootBulkIn === undefined) {
        console.log("Unable to find the picoboot interface");
        return;
      }

      await device.open();
      await device.claimInterface(picobootInterface.interfaceNumber);

      const logCommandStatus = async () => {
        let commandStatus = (await device.controlTransferIn({
          requestType: 'vendor',
          recipient: 'interface',
          request: 0b01000010,
          value: 0,
          index: picobootInterface.interfaceNumber,
        }, 16)).data;
        
        console.log('dToken', commandStatus.getUint32(0, true));
        console.log('dStatusCode', commandStatus.getUint32(4, true));
        console.log('bCmdId', commandStatus.getUint8(8));
        console.log('bInProgress', commandStatus.getUint8(9));
      };

      if (wasReset) {
        // Disable the USB mass storage interface
        console.log("Disabling usb interface");
        setMessage("Disabling usb interface");
        const exclusiveAccessCommandBuf = new Uint8Array(32);
        const exclusiveAccessCommand = new DataView(exclusiveAccessCommandBuf.buffer)
        exclusiveAccessCommand.setUint32(0x00, 0x431fd10b, true); // dMagic
        exclusiveAccessCommand.setUint32(0x04, 1, true);          // dToken
        exclusiveAccessCommand.setUint32(0x08, 1, true);          // bCmdId - EXCLUSIVE_ACCESS
        exclusiveAccessCommand.setUint8(0x09, 1);                 // bCmdSize
        exclusiveAccessCommand.setUint32(0x0c, 0, true);          // dTransferLength
        exclusiveAccessCommand.setUint8(0x10, 2);                 // bExclusive (args) - EXCLUSIVE_AND_EJECT
        await device.transferOut(
          picobootBulkOut.endpointNumber,
          exclusiveAccessCommandBuf,
        );

        await device.transferIn(
          picobootBulkIn.endpointNumber,
          32,
        );
      }

      // Exit XIP mode, we need to do this before erasing the flash otherwise it'll get stuck
      console.log("Disabling XIP");
      setMessage("Disabling XIP");
      const exitXipCommandBuf = new Uint8Array(32);
      const exitXipCommand = new DataView(exitXipCommandBuf.buffer);
      exitXipCommand.setUint32(0x00, 0x431fd10b, true);     // dMagic
      exitXipCommand.setUint32(0x04, 2, true);              // dToken
      exitXipCommand.setUint32(0x08, 6, true);              // bCmdId - EXIT_XIP
      exitXipCommand.setUint8(0x09, 0);                     // bCmdSize
      exitXipCommand.setUint32(0x0c, 0, true);              // dTransferLength
      await device.transferOut(
        picobootBulkOut.endpointNumber,
        exitXipCommandBuf,
      );

      await device.transferIn(
        picobootBulkIn.endpointNumber,
        32,
      );

      // Erase flash
      console.log("Erasing flash");
      setMessage("Erasing flash");
      const MAX_SECTORS_PER_ERASE = 10;
      const sectorsToErase = maxSector - minSector + 1;
      let eraseStartAddr = minSector * FLASH_SECTOR_SIZE;
      let sectorsErased = 0
      while (sectorsErased < sectorsToErase) {
        const eraseSectorCount = Math.min(sectorsToErase - sectorsErased, MAX_SECTORS_PER_ERASE);
        const eraseByteCount = FLASH_SECTOR_SIZE * eraseSectorCount;

        const flashEraseCommandBuf = new Uint8Array(32);
        const flashEraseCommand = new DataView(flashEraseCommandBuf.buffer);
        flashEraseCommand.setUint32(0x00, 0x431fd10b, true);     // dMagic
        flashEraseCommand.setUint32(0x04, 3, true);              // dToken
        flashEraseCommand.setUint32(0x08, 3, true);              // bCmdId - FLASH_ERASE
        flashEraseCommand.setUint8(0x09, 8);                     // bCmdSize
        flashEraseCommand.setUint32(0x0c, 0, true);              // dTransferLength
        flashEraseCommand.setUint32(0x10, eraseStartAddr, true); // dAddr (args)
        flashEraseCommand.setUint32(0x14, eraseByteCount, true); // dSize (args)
        await device.transferOut(
          picobootBulkOut.endpointNumber,
          flashEraseCommand,
        );
  
        await device.transferIn(
          picobootBulkIn.endpointNumber,
          32,
        );

        eraseStartAddr += eraseByteCount;
        sectorsErased += eraseSectorCount;
        console.log(`Erased ${sectorsErased}/${sectorsToErase} sectors (${eraseSectorCount})`);
        setMessage(`Erased ${sectorsErased}/${sectorsToErase} sectors (${eraseSectorCount})`);
      }
      
      // Write firmware to flash
      console.log("Writing to flash");
      setMessage("Writing to flash");
      const MAX_PAGES_PER_WRITE = 4;
      let writeStartAddr = minSector * FLASH_SECTOR_SIZE;
      let pagesWritten = 0;
      while (pagesWritten < numBlocks) {
        const writePageCount = Math.min(numBlocks - pagesWritten, MAX_PAGES_PER_WRITE);
        const writeByteCount = writePageCount * PAGE_SIZE;

        const writeCommandBuf = new Uint8Array(32);
        const writeCommand = new DataView(writeCommandBuf.buffer);
        writeCommand.setUint32(0x00, 0x431fd10b, true);     // dMagic
        writeCommand.setUint32(0x04, 4, true);              // dToken
        writeCommand.setUint8(0x08, 5);                     // bCmdId - WRITE
        writeCommand.setUint8(0x09, 8);                     // bCmdSize
        writeCommand.setUint32(0x0c, writeByteCount, true); // dTransferLength
        writeCommand.setUint32(0x10, writeStartAddr, true); // dAddr (args)
        writeCommand.setUint32(0x14, writeByteCount, true); // dSize (args)
        await device.transferOut(
          picobootBulkOut.endpointNumber,
          writeCommandBuf,
        );

        const firmwareOffset = pagesWritten*PAGE_SIZE;
        await device.transferOut(
          picobootBulkOut.endpointNumber,
          firmwareWriteBuf.subarray(
            firmwareOffset,
            firmwareOffset + writeByteCount,
          ),
        );
  
        await device.transferIn(
          picobootBulkIn.endpointNumber,
          32,
        );

        writeStartAddr += writeByteCount;
        pagesWritten += writePageCount;
        console.log(`Wrote ${pagesWritten}/${numBlocks} pages (${writePageCount})`);
        setMessage(`Wrote ${pagesWritten}/${numBlocks} pages (${writePageCount})`);
      }

      // Verify contents of flash (readback)
      console.log("Verifying flash contents");
      setMessage("Verifying flash contents");
      const MAX_PAGES_PER_READ = 4;
      let readStartAddr = minSector * FLASH_SECTOR_SIZE;
      let pagesRead = 0;
      while (pagesRead < numBlocks) {
        const readPageCount = Math.min(numBlocks - pagesRead, MAX_PAGES_PER_READ);
        const readByteCount = readPageCount * PAGE_SIZE;

        const readCommandBuf = new Uint8Array(32);
        const readCommand = new DataView(readCommandBuf.buffer);
        readCommand.setUint32(0x00, 0x431fd10b, true);    // dMagic
        readCommand.setUint32(0x04, 5, true);             // dToken
        readCommand.setUint8(0x08, 0x84);                 // bCmdId - READ
        readCommand.setUint8(0x09, 8);                    // bCmdSize
        readCommand.setUint32(0x0c, readByteCount, true); // dTransferLength
        readCommand.setUint32(0x10, readStartAddr, true); // dAddr (args)
        readCommand.setUint32(0x14, readByteCount, true); // dSize (args)
        await device.transferOut(
          picobootBulkOut.endpointNumber,
          readCommandBuf,
        );

        const readResult = (await device.transferIn(
          picobootBulkIn.endpointNumber,
          readByteCount,
        )).data;

        const firmwareOffset = pagesRead*PAGE_SIZE;
        const expectedData = firmwareWriteBuf.subarray(
          firmwareOffset,
          firmwareOffset + readByteCount,
        );
        for (let j = 0; j < readByteCount; j++) {
          const expected = expectedData[j];
          const found = readResult.getUint8(j);
          if (found != expected) {
            console.warn(`Error at ${readStartAddr + j}: expected ${expected}, found ${found}`);
            setMessage(`Error at ${readStartAddr + j}: expected ${expected}, found ${found}`);

            await device.close();
            return;
          }
        }

        // Empty packet to ack the read
        await device.transferOut(
          picobootBulkOut.endpointNumber,
          new Uint8Array(),
        );

        readStartAddr += readByteCount;
        pagesRead += readPageCount;
        console.log(`Read ${pagesRead}/${numBlocks} pages (${readPageCount})`);
        setMessage(`Read ${pagesRead}/${numBlocks} pages (${readPageCount})`);
      }

      // Re-enable XIP before rebooting into the user code
      console.log("Enabling XIP");
      setMessage("Enabling XIP");
      const enterXipCommandBuf = new Uint8Array(32);
      const enterXipCommand = new DataView(enterXipCommandBuf.buffer);
      enterXipCommand.setUint32(0x00, 0x431fd10b, true);     // dMagic
      enterXipCommand.setUint32(0x04, 2, true);              // dToken
      enterXipCommand.setUint32(0x08, 7, true);              // bCmdId - ENTER_XIP
      enterXipCommand.setUint8(0x09, 0);                     // bCmdSize
      enterXipCommand.setUint32(0x0c, 0, true);              // dTransferLength
      await device.transferOut(
        picobootBulkOut.endpointNumber,
        enterXipCommandBuf,
      );

      await device.transferIn(
        picobootBulkIn.endpointNumber,
        32,
      );

      // Reboot into user code
      console.log("Rebooting");
      setMessage("Rebooting");
      const resetCommandBuf = new Uint8Array(32);
      const resetCommand = new DataView(resetCommandBuf.buffer)
      resetCommand.setUint32(0x00, 0x431fd10b, true); // dMagic
      resetCommand.setUint32(0x04, 6, true);          // dToken
      resetCommand.setUint32(0x08, 2, true);          // bCmdId - REBOOT
      resetCommand.setUint8(0x09, 0x0c);              // bCmdSize
      resetCommand.setUint32(0x0c, 0, true);          // dTransferLength
      resetCommand.setUint32(0x10, 0, true);          // dPC (args)
      resetCommand.setUint32(0x14, 0, true);          // dSP (args)
      resetCommand.setUint32(0x18, 0, true);          // dDelayMS (args)
      await device.transferOut(
        picobootBulkOut.endpointNumber,
        resetCommandBuf,
      );

      await device.transferIn(
        picobootBulkIn.endpointNumber,
        32,
      );

      await logCommandStatus();

      // Disconnecting from device
      console.log("Disconnecting from device...");
      setMessage("Disconnecting from device...");
      await device.close();

      console.log("Done!");
      setMessage("Done!");
    } else {
      alert(`Unknown productId: ${device.productId}`);
    }
  }, []);

  const onClickReset = useCallback(async () => {
    // Request the device
    let device;
    try {
      device = await navigator.usb.requestDevice({
        filters: [{
          vendorId: VENDOR_ID_RASPBERRY_PI,
        }],
      });
    } catch {
      return;
    }

    // Automatically reset the device if it is in the application code
    if (device.productId === PRODUCT_ID_RP2040_STDIO_USB) {
      console.log("Device appears to be a RP2040 device with a USB serial connection, not in BOOTSEL mode. Rebooting into BOOTSEL mode...");

      const picoUsbResetInterface = device.configuration.interfaces
        .find(iface => (
          (iface.alternate.interfaceClass === 0xFF) &&
          (iface.alternate.interfaceSubclass === RESET_INTERFACE_SUBCLASS) &&
          (iface.alternate.interfaceProtocol === RESET_INTERFACE_PROTOCOL)
        ));

      if (picoUsbResetInterface === undefined) {
        console.log("Unable to find the pico usb reset interface");
        setMessage("Unable to find the pico usb reset interface");
        return;
      }

      await device.open();
      await device.claimInterface(picoUsbResetInterface.interfaceNumber);

      // NOTE(Jon @ Fyelabs): This fails since the device reboots
      try {
        await device.controlTransferOut({
          requestType: 'class',
          recipient: 'interface',
          request: RESET_REQUEST_BOOTSEL,
          value: 0,
          index: picoUsbResetInterface.interfaceNumber,
        });
      } catch {}

      setMessage("Device reset into DFU!");
    }
  }, []);

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      maxWidth: "160px",
      gap: "8px",
      margin: "auto",
    }}>
      {/* <button onClick={onClickFlash}>Flash Firmware</button> */}
      <button onClick={onClickReset}>Reset into DFU</button>
      <a href="/percy_2025_09_17.uf2" download>Download Firmware</a>
      <p>{message}</p>
    </div>
  );
};

export default Firmware;